import React from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogDescription,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const WIDTHS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-5xl',
};

/**
 * Viewport-safe shell for centered application dialogs.
 *
 * The panel is a flex column bounded by the dynamic viewport. Only the body
 * scrolls, leaving the heading, close control, and footer actions reachable.
 */
export default function ModalShell({
  open = true,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'xl',
  closeDisabled = false,
  closeOnBackdrop = true,
  closeOnEscape = true,
  initialFocus,
  className = '',
  bodyClassName = '',
  headerClassName = '',
  footerClassName = '',
  zIndexClassName = 'z-50',
}) {
  const requestClose = () => {
    if (!closeDisabled && onClose) onClose();
  };

  const handleDialogClose = () => {
    if (!closeOnEscape) return;
    requestClose();
  };

  const handleBackdropClick = () => {
    if (closeOnBackdrop) requestClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      initialFocus={initialFocus}
      className={`relative ${zIndexClassName}`}
    >
      <DialogBackdrop
        className="fixed inset-0 bg-slate-900/70"
        onClick={handleBackdropClick}
        data-testid="modal-backdrop"
      />
      <div className="viewport-dialog fixed inset-0 overflow-hidden p-4 sm:p-8">
        <div className="flex h-full items-center justify-center">
          <DialogPanel
            data-testid="modal-panel"
            className={`flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full ${WIDTHS[width] || width} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[calc(100vh-4rem)] sm:max-h-[calc(100dvh-4rem)] ${className}`}
          >
            {(title || description || onClose) && (
              <header className={`flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 ${headerClassName}`}>
                <div className="min-w-0">
                  {title && <DialogTitle className="text-lg font-semibold text-slate-950">{title}</DialogTitle>}
                  {description && <DialogDescription className="mt-1 text-sm text-slate-600">{description}</DialogDescription>}
                </div>
                {onClose && (
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={closeDisabled}
                    aria-label="Close"
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                )}
              </header>
            )}
            <div data-testid="modal-body" className={`min-h-0 flex-1 overflow-y-auto px-6 py-5 ${bodyClassName}`}>
              {children}
            </div>
            {footer && (
              <footer className={`shrink-0 border-t border-slate-200 bg-white px-6 py-4 ${footerClassName}`}>
                {footer}
              </footer>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
